import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { BsThreeDotsVertical } from 'react-icons/bs';

import SideNav from '../SideNav';

import {
  fetchOutsourceRequests,
  createOutsourceRequest,
  updateOutsourceRequest,
  deleteOutsourceRequest,
  fetchUserProfile,
} from '../../../services/api';

import OutsourceProgressBar from './OutsourceProgressBar';
import OutsourceRequestProgress from './OutsourceRequestProgress';

const OutsourceRequests = () => {
  const [outsourceRequests, setOutsourceRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [newRequest, setNewRequest] = useState({ title: '', nda_link: '' });
  const [userProfile, setUserProfile] = useState(null);
  const [progressTriggers, setProgressTriggers] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const profile = await fetchUserProfile(token);
        setUserProfile(profile);
        const requests = await fetchOutsourceRequests();
        setOutsourceRequests(requests);
      } catch (err) {
        console.error('Error loading outsource requests:', err);
      }
    };

    if (token) {
      loadData();
    }
  }, [token]);

  const handleAddRequest = async () => {
    try {
      const created = await createOutsourceRequest(newRequest);
      setOutsourceRequests(prev => [created, ...prev]);
      resetRequestModal();
    } catch (err) {
      console.error('Error adding request:', err);
      alert('Error: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleUpdateRequest = async () => {
    try {
      const updated = await updateOutsourceRequest(editingRequest.id, newRequest);
      setOutsourceRequests(prev =>
        prev.map(r => (r.id === updated.id ? updated : r))
      );
      resetRequestModal();
    } catch (err) {
      console.error('Error updating request:', err);
      alert('Error: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const confirmDeleteRequest = (request) => {
    setRequestToDelete(request);
    setShowDeleteModal(true);
  };

  const handleDeleteRequest = async () => {
    try {
      await deleteOutsourceRequest(requestToDelete.id);
      setOutsourceRequests(prev => prev.filter(r => r.id !== requestToDelete.id));
      setShowDeleteModal(false);
      setRequestToDelete(null);
    } catch (err) {
      console.error('Error deleting request:', err);
      alert('Error deleting request');
    }
  };

  const resetRequestModal = () => {
    setShowRequestModal(false);
    setEditingRequest(null);
    setNewRequest({ title: '', nda_link: '' });
  };

  const handleLogout = () => {
    navigate('/logout');
  };

  const triggerProgressUpdate = (requestId) => {
    setProgressTriggers(prev => ({
      ...prev,
      [requestId]: Date.now(),
    }));
  };

  return (
    <div className="container-fluid dashboard-background">
      <div className="row">
        <SideNav userProfile={userProfile} handleLogout={handleLogout} />
        <div className="col-md-10 mt-3" style={{ minHeight: '100vh' }}>
          <div className="text-center mb-4 d-flex align-items-center justify-content-center">
            <img
              src="https://res.cloudinary.com/dnbbm9vzi/image/upload/v1747769920/logo_oq3zwy.png"
              alt="Logo"
              style={{ width: '50px', height: '50px', marginRight: '10px' }}
            />
            <h1 className="d-inline mb-0 fw-bold">Digis Customer Portal</h1>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className='fw-bold mt-3'>Product Estimation Status</h3>
            <Button onClick={() => setShowRequestModal(true)} className="fw-bold d-flex align-items-center">
              <span className="me-2 text-white">+</span>Add Project
            </Button>
          </div>

          {outsourceRequests.length > 0 && (
            <div className="d-flex flex-column gap-4">
              {outsourceRequests.map((req) => (
                <div key={req.id} className="w-100 border rounded p-3" style={{ backgroundColor: '#EFF2FC' }}>
                  <div className="d-flex flex-row gap-4 align-items-start">
                    <div style={{ minWidth: '25%' }}>
                      <div className="alert d-flex justify-content-between"
                        style={{
                          backgroundColor: '#3F8CFF',
                          color: '#333',
                          borderRadius: '0.45rem',
                          padding: '1rem'
                        }}
                      >
                        <div className='text-white'>
                          <strong>Project Confidentiality</strong>
                          <div><strong>Title:</strong> {req.title}</div>
                          <a href={req.nda_link} target="_blank" rel="noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>{req.title}</a>
                        </div>
                        <Dropdown>
                          <Dropdown.Toggle variant="light" size="sm" className="no-arrow">
                            <BsThreeDotsVertical />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => {
                              setEditingRequest(req);
                              setNewRequest({ title: req.title, nda_link: req.nda_link });
                              setShowRequestModal(true);
                            }}>
                              Edit Project
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => confirmDeleteRequest(req)}>
                              Delete Project
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>

                    <div className="flex-grow-1 mb-4">
                      <OutsourceProgressBar
                        requestId={req.id}
                        trigger={progressTriggers[req.id]}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <OutsourceRequestProgress
                      requestId={req.id}
                      onStageChange={() => triggerProgressUpdate(req.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add/Edit Modal */}
          <Modal show={showRequestModal} onHide={resetRequestModal}>
            <Modal.Header closeButton>
              <Modal.Title className="w-100 text-center fw-bold">
                {editingRequest ? 'Edit Project' : 'Add Project'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={newRequest.title}
                    onChange={e => setNewRequest({ ...newRequest, title: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>NDA Link</Form.Label>
                  <Form.Control
                    type="text"
                    value={newRequest.nda_link}
                    onChange={e => setNewRequest({ ...newRequest, nda_link: e.target.value })}
                  />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="dark" onClick={resetRequestModal} className="fw-bold">Cancel</Button>
              <Button
                variant="primary"
                onClick={editingRequest ? handleUpdateRequest : handleAddRequest}
                className="fw-bold"
              >
                {editingRequest ? 'Update' : 'Add'} Project
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
            <Modal.Header closeButton className="justify-content-center">
              <Modal.Title className="w-100 text-center fw-bold">Confirm Deletion</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className="text-center">
                Are you sure you want to delete the project <strong>{requestToDelete?.title}</strong>?
              </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="dark fw-bold" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button variant="danger fw-bold" onClick={handleDeleteRequest}>Delete</Button>
            </Modal.Footer>
          </Modal>

        </div>
      </div>
    </div>
  );
};

export default OutsourceRequests;

